import { Picker } from '@react-native-picker/picker';
import React, { useCallback, useEffect, useState } from 'react';
import { Text, TextInput, View } from 'react-native';

import AppStyles from '@/constants/Styles';
import { COUNTRY_CODES, CountryCode, getDefaultCountry } from '@/constants/countryCodes';
import { getPhoneNumberErrorMessage, isValidPhoneNumber, normalizePhoneNumber } from '@/utils/phoneValidation';

interface PhoneNumberInputProps {
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  style?: any;
  placeholderTextColor?: string;
  onValidationChange?: (isValid: boolean, errorMessage?: string) => void;
  onBlur?: () => void;
}

export function PhoneNumberInput({
  value,
  onChangeText,
  placeholder = "Phone Number",
  style,
  placeholderTextColor = '#999',
  onValidationChange,
  onBlur,
}: PhoneNumberInputProps) {
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(getDefaultCountry());
  const [phoneNumber, setPhoneNumber] = useState('');
  
  // Parse the current value to extract country and phone number
  useEffect(() => {
    if (value) {
      // Try to extract country code from the value
      const normalized = normalizePhoneNumber(value);
      const dialCodeMatch = COUNTRY_CODES.find(country => 
        normalized.startsWith(country.dialCode)
      );
      
      if (dialCodeMatch) {
        setSelectedCountry(dialCodeMatch);
        const phonePart = normalized.replace(dialCodeMatch.dialCode, '');
        setPhoneNumber(phonePart);
      } else {
        // Default to UK if no country code found
        setSelectedCountry(getDefaultCountry());
        setPhoneNumber(normalized.replace('+44', ''));
      }
    } else {
      // Reset to default when value is empty
      setSelectedCountry(getDefaultCountry());
      setPhoneNumber('');
    }
  }, [value]);

  // Derive validation state - no useState needed!
  const fullNumber = phoneNumber ? `${selectedCountry.dialCode}${phoneNumber}` : '';
  const isValidPhone = phoneNumber ? isValidPhoneNumber(fullNumber) : false;
  const errorMessage = phoneNumber && !isValidPhone ? getPhoneNumberErrorMessage(fullNumber) : undefined;
  const isValid = isValidPhone || !phoneNumber;

  // Only useEffect for the actual SIDE EFFECT (parent callback)
  useEffect(() => {
    onValidationChange?.(isValid, errorMessage);
  }, [isValid, errorMessage, onValidationChange]);

  const handleCountryChange = useCallback((countryCode: string) => {
    const country = COUNTRY_CODES.find(c => c.code === countryCode) || getDefaultCountry();
    onChangeText(`${country.dialCode}${phoneNumber}`);
  }, [phoneNumber, onChangeText]);

  const handlePhoneNumberChange = useCallback((text: string) => {
    // Remove any non-digit characters except + at the beginning
    const cleaned = text.replace(/[^\d]/g, '');
    onChangeText(`${selectedCountry.dialCode}${cleaned}`);
  }, [selectedCountry, onChangeText]);

  return (
    <View style={[AppStyles.phoneNumberContainer, style]}>
      <View style={AppStyles.phoneNumberRow}>
        {/* Country Code Picker */}
        <View style={AppStyles.countryCodeContainer}>
          <View style={AppStyles.countryCodePicker}>
            <Picker
              selectedValue={selectedCountry.code}
              onValueChange={handleCountryChange}
              style={AppStyles.countryCodePickerInput}
              dropdownIconColor="#007AFF"
            >
              {COUNTRY_CODES.map((country) => (
                <Picker.Item
                  key={country.code}
                  label={country.dialCode}
                  value={country.code}
                />
              ))}
            </Picker>
          </View>
        </View>

        {/* Phone Number Input */}
        <View style={AppStyles.phoneNumberInputContainer}>
          <TextInput
            placeholder={placeholder}
            value={phoneNumber}
            onChangeText={handlePhoneNumberChange}
            onBlur={onBlur}
            keyboardType="phone-pad"
            style={[
              AppStyles.phoneNumberInput,
              !isValid && AppStyles.phoneNumberInputError
            ]}
            placeholderTextColor={placeholderTextColor}
            accessibilityLabel="Phone number"
            accessibilityHint="Enter your phone number without the country code"
          />
        </View>
      </View>
      
      {/* Error Message */}
      {!isValid && errorMessage && (
        <Text style={AppStyles.phoneNumberErrorText}>{errorMessage}</Text>
      )}
    </View>
  );
}

export default PhoneNumberInput;
